import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import { PageContainer, Section } from '../../components/AdminContentWrapper';
import RequireBillingRole from './components/RequireBillingRole';
import CreditsEngineService, { toNumber } from '../../../../services/CreditsEngineService';
import TablePaginationBar from '../../../../components/common/TablePaginationBar';
import { useTablePagination } from '../../../../hooks';
import HelpIcon from '../../../../components/help/HelpIcon';

const darkTextFieldStyle = {
  '& .MuiOutlinedInput-root': {
    color: 'var(--wb-text-strong)',
    '& fieldset': { borderColor: 'var(--wb-outline)' },
    '&:hover fieldset': { borderColor: 'var(--wb-text-dim)' }
  },
  '& .MuiInputLabel-root': { color: 'var(--wb-text-soft)' }
};

const cellSx = { color: 'var(--wb-text)', borderColor: 'var(--wb-border)' };
const headSx = { ...cellSx, color: 'var(--wb-muted-85)', fontWeight: 600 };

// Margen bruto del crédito: el cobro es créditos = costo_interno_real /
// target_internal_cost_per_credit, y cada crédito se vende a
// user_value_per_credit_usd -- así que la ganancia es la misma proporción
// para cualquier modelo u operación (los precios de "Modelos configurados"
// son costo puro de AWS, sin sobreprecio). null si los valores no son
// válidos (vacíos o <= 0).
export const creditMargin = (userValuePerCredit, targetCostPerCredit) => {
  const value = Number(userValuePerCredit);
  const target = Number(targetCostPerCredit);
  if (!(value > 0) || !(target > 0)) return null;
  return {
    margin: 1 - target / value,
    creditsPerDollarOfCost: 1 / target,
    chargePerDollarOfCost: value / target
  };
};

export const formatMargin = (margin) => (margin == null ? '—' : `${(margin * 100).toFixed(1).replace(/\.0$/, '')} %`);

const marginColor = (margin) => (margin != null && margin <= 0 ? 'var(--wb-danger)' : 'var(--wb-text)');

const emptyForm = {
  scope: '',
  user_value_per_credit_usd: '1.00',
  target_internal_cost_per_credit: '0.25',
  minimum_charge: '0.01',
  rounding_precision: '4',
  allow_overage: false,
  overage_credit_price: '',
  allow_rollover: false,
  rollover_limit: ''
};

const BillingCreditConfigContent = () => {
  const [configs, setConfigs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [formOpen, setFormOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formValues, setFormValues] = useState(emptyForm);
  const [isSaving, setIsSaving] = useState(false);

  const [cursor, setCursor] = useState(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Versionado por effective_from -- mostrar todas las versiones, más reciente primero.
  const porVersionDescendente = (filas) =>
    [...filas].sort((a, b) => String(b.effective_from || '').localeCompare(String(a.effective_from || '')));

  // Paginación con cursor del backend: `load` trae la primera página y
  // `loadMore` va apilando las siguientes. El pager numerado opera sobre lo
  // acumulado; useTablePagination llama a `loadMore` cuando el usuario
  // entra a la última página cargada y todavía queda cursor.
  const load = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const { items, nextCursor } = await CreditsEngineService.listCreditConfig();
      setConfigs(porVersionDescendente(items));
      setCursor(nextCursor);
    } catch (err) {
      setError(err.message || 'No fue posible obtener la configuración de créditos.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadMore = useCallback(async () => {
    if (!cursor || isLoadingMore) return;
    setIsLoadingMore(true);
    try {
      const { items, nextCursor } = await CreditsEngineService.listCreditConfig({ cursor });
      setConfigs((prev) => porVersionDescendente([...prev, ...items]));
      setCursor(nextCursor);
    } catch (err) {
      setError(err.message || 'No fue posible obtener más versiones de configuración.');
    } finally {
      setIsLoadingMore(false);
    }
  }, [cursor, isLoadingMore]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setIsEditing(false);
    setFormValues({ ...emptyForm, scope: 'GLOBAL' });
    setFormOpen(true);
  };

  const openEdit = (item) => {
    setIsEditing(true);
    setFormValues({
      scope: item.config_scope,
      user_value_per_credit_usd: String(toNumber(item.user_value_per_credit_usd, 1)),
      target_internal_cost_per_credit: String(toNumber(item.target_internal_cost_per_credit, 0.25)),
      minimum_charge: String(toNumber(item.minimum_charge, 0.01)),
      rounding_precision: String(item.rounding_precision != null ? item.rounding_precision : 4),
      allow_overage: Boolean(item.allow_overage),
      overage_credit_price: item.overage_credit_price != null ? String(toNumber(item.overage_credit_price)) : '',
      allow_rollover: Boolean(item.allow_rollover),
      rollover_limit: item.rollover_limit != null ? String(toNumber(item.rollover_limit)) : ''
    });
    setFormOpen(true);
  };

  const handleChange = (field) => (event) => {
    setFormValues((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleToggle = (field) => (event) => {
    setFormValues((prev) => ({ ...prev, [field]: event.target.checked }));
  };

  const handleSave = async () => {
    if (!formValues.scope.trim()) {
      setSnackbar({ open: true, message: 'El scope es requerido ("GLOBAL" o un account_id).', severity: 'error' });
      return;
    }
    const payload = {
      scope: formValues.scope.trim(),
      user_value_per_credit_usd: Number(formValues.user_value_per_credit_usd || 1),
      target_internal_cost_per_credit: Number(formValues.target_internal_cost_per_credit || 0.25),
      minimum_charge: Number(formValues.minimum_charge || 0.01),
      rounding_precision: Number(formValues.rounding_precision || 4),
      allow_overage: formValues.allow_overage,
      allow_rollover: formValues.allow_rollover
    };
    if (formValues.allow_overage && formValues.overage_credit_price !== '') {
      payload.overage_credit_price = Number(formValues.overage_credit_price);
    }
    if (formValues.allow_rollover && formValues.rollover_limit !== '') {
      payload.rollover_limit = Number(formValues.rollover_limit);
    }
    try {
      setIsSaving(true);
      await CreditsEngineService.upsertCreditConfig(payload);
      setSnackbar({ open: true, message: 'Configuración guardada correctamente.', severity: 'success' });
      setFormOpen(false);
      load();
    } catch (err) {
      setSnackbar({ open: true, message: err.message || 'No se pudo guardar la configuración.', severity: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const { pageRows, paginationProps } = useTablePagination(configs, {
    storageKey: 'billingCreditConfig',
    hasMore: Boolean(cursor),
    isLoadingMore,
    onNeedMore: loadMore
  });

  return (
    <PageContainer
      title="Configuración de créditos"
      description={
        'Define cuánto vale 1 crédito para el usuario (USD) y a qué costo interno objetivo se calcula el consumo real — el ' +
        'margen queda embebido en cuántos créditos se descuentan por invocación. Configura "GLOBAL" para el default de la ' +
        'plataforma, o un account_id específico para condiciones negociadas por tenant. Cada guardado crea una nueva versión.'
      }
      actions={
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={load} disabled={isLoading}
            sx={{ borderColor: 'var(--wb-blue-40)', color: 'var(--wb-blue)', '&:hover': { borderColor: 'var(--wb-blue)' } }}>
            Actualizar
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}
            sx={{ bgcolor: 'var(--wb-primary)', '&:hover': { bgcolor: 'var(--wb-primary-hover)' } }}>
            Nueva configuración
          </Button>
        </Stack>
      }
    >
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          <AlertTitle>Error al cargar datos</AlertTitle>
          {error}
        </Alert>
      )}

      <Section title="Scopes configurados" subtitle='"GLOBAL" es el default de la plataforma; un account_id sobrescribe el default solo para ese tenant.'>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} sx={{ color: 'var(--wb-primary)' }} />
          </Box>
        ) : configs.length === 0 ? (
          <Typography sx={{ color: 'var(--wb-muted-70)', py: 3, textAlign: 'center' }}>
            Todavía no hay configuración de créditos — se están usando los defaults internos.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={headSx}>Scope</TableCell>
                  <TableCell sx={headSx} align="right">Valor/crédito (usuario)</TableCell>
                  <TableCell sx={headSx} align="right">Costo interno objetivo/crédito</TableCell>
                  <TableCell sx={headSx} align="right">Margen</TableCell>
                  <TableCell sx={headSx} align="right">Cargo mínimo</TableCell>
                  <TableCell sx={headSx}>Overage</TableCell>
                  <TableCell sx={headSx}>Rollover</TableCell>
                  <TableCell sx={headSx}>Vigente desde</TableCell>
                  <TableCell sx={headSx} align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pageRows.map((item) => (
                  <TableRow key={`${item.config_scope}#${item.effective_from}`} hover>
                    <TableCell sx={cellSx}>
                      {item.config_scope === 'GLOBAL' ? (
                        <Chip size="small" label="GLOBAL" sx={{ backgroundColor: 'var(--wb-accent-tint)', color: 'var(--wb-accent)' }} />
                      ) : (
                        item.config_scope
                      )}
                    </TableCell>
                    <TableCell sx={cellSx} align="right">${toNumber(item.user_value_per_credit_usd, 1).toFixed(2)}</TableCell>
                    <TableCell sx={cellSx} align="right">${toNumber(item.target_internal_cost_per_credit, 0.25).toFixed(4)}</TableCell>
                    {(() => {
                      const info = creditMargin(
                        toNumber(item.user_value_per_credit_usd, 1),
                        toNumber(item.target_internal_cost_per_credit, 0.25)
                      );
                      return (
                        <TableCell sx={{ ...cellSx, color: marginColor(info?.margin) }} align="right">
                          {formatMargin(info?.margin)}
                        </TableCell>
                      );
                    })()}
                    <TableCell sx={cellSx} align="right">${toNumber(item.minimum_charge, 0.01).toFixed(2)}</TableCell>
                    <TableCell sx={cellSx}>{item.allow_overage ? 'Permitido' : 'No permitido'}</TableCell>
                    <TableCell sx={cellSx}>{item.allow_rollover ? 'Permitido' : 'No permitido'}</TableCell>
                    <TableCell sx={cellSx}>{item.effective_from || '—'}</TableCell>
                    <TableCell sx={cellSx} align="right">
                      <Tooltip title="Editar (crea nueva versión)">
                        <IconButton size="small" onClick={() => openEdit(item)} sx={{ color: 'var(--wb-blue)' }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {configs.length > 0 && <TablePaginationBar {...paginationProps} />}
      </Section>

      <Dialog open={formOpen} onClose={() => !isSaving && setFormOpen(false)} PaperProps={{ sx: { backgroundColor: 'var(--wb-bg-surface-alt)', color: 'var(--wb-text)', minWidth: { xs: '90%', sm: 480 } } }}>
        <DialogTitle>{isEditing ? 'Editar configuración (nueva versión)' : 'Nueva configuración de crédito'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label='Scope ("GLOBAL" o un account_id)'
              value={formValues.scope}
              onChange={handleChange('scope')}
              disabled={isEditing}
              fullWidth
              size="small"
              sx={darkTextFieldStyle}
              InputProps={{ endAdornment: (
                <InputAdornment position="end"><HelpIcon module="creditos" topic="configuracion-de-creditos" anchor="scope-global-o-un-account_id" /></InputAdornment>
              ) }}
            />
            <TextField
              label="Valor de 1 crédito para el usuario (USD)"
              value={formValues.user_value_per_credit_usd}
              onChange={handleChange('user_value_per_credit_usd')}
              type="number"
              inputProps={{ step: '0.01', min: 0 }}
              fullWidth
              size="small"
              sx={darkTextFieldStyle}
              InputProps={{ endAdornment: (
                <InputAdornment position="end"><HelpIcon module="creditos" topic="configuracion-de-creditos" anchor="valor-de-1-crédito-para-el-usuario-usd" /></InputAdornment>
              ) }}
            />
            <TextField
              label="Costo interno objetivo por crédito (USD)"
              value={formValues.target_internal_cost_per_credit}
              onChange={handleChange('target_internal_cost_per_credit')}
              type="number"
              inputProps={{ step: '0.0001', min: 0 }}
              helperText="créditos_cobrados = costo_interno_real / este valor. Con el default (0.25) un costo real de $1 cobra 4 créditos."
              fullWidth
              size="small"
              sx={darkTextFieldStyle}
              InputProps={{ endAdornment: (
                <InputAdornment position="end"><HelpIcon module="creditos" topic="configuracion-de-creditos" anchor="costo-interno-objetivo-por-crédito-usd" /></InputAdornment>
              ) }}
            />
            {(() => {
              const info = creditMargin(formValues.user_value_per_credit_usd, formValues.target_internal_cost_per_credit);
              if (!info) return null;
              return (
                <Box
                  data-testid="credit-margin-preview"
                  sx={{ p: 1.5, borderRadius: 1, border: '1px solid var(--wb-border)', backgroundColor: 'var(--wb-accent-tint)' }}
                >
                  <Typography variant="body2" sx={{ color: marginColor(info.margin), fontWeight: 600 }}>
                    Margen bruto: {formatMargin(info.margin)}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'var(--wb-text-soft)', display: 'block' }}>
                    Un costo real de $1.00 cobra {info.creditsPerDollarOfCost.toFixed(2).replace(/\.00$/, '')} créditos = ${info.chargePerDollarOfCost.toFixed(2)} para el cliente.
                    {info.margin <= 0 && ' El crédito se vende por debajo de su costo.'}
                  </Typography>
                </Box>
              );
            })()}
            <TextField
              label="Cargo mínimo por invocación (créditos)"
              value={formValues.minimum_charge}
              onChange={handleChange('minimum_charge')}
              type="number"
              inputProps={{ step: '0.01', min: 0 }}
              fullWidth
              size="small"
              sx={darkTextFieldStyle}
              InputProps={{ endAdornment: (
                <InputAdornment position="end"><HelpIcon module="creditos" topic="configuracion-de-creditos" anchor="cargo-mínimo-por-invocación-créditos" /></InputAdornment>
              ) }}
            />
            <TextField
              label="Precisión de redondeo (decimales)"
              value={formValues.rounding_precision}
              onChange={handleChange('rounding_precision')}
              type="number"
              inputProps={{ step: '1', min: 0, max: 8 }}
              fullWidth
              size="small"
              sx={darkTextFieldStyle}
              InputProps={{ endAdornment: (
                <InputAdornment position="end"><HelpIcon module="creditos" topic="configuracion-de-creditos" anchor="precisión-de-redondeo-decimales" /></InputAdornment>
              ) }}
            />
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <FormControlLabel
                control={<Switch checked={formValues.allow_overage} onChange={handleToggle('allow_overage')} />}
                label="Permitir sobregiro (overage)"
              />
              <HelpIcon module="creditos" topic="configuracion-de-creditos" anchor="permitir-sobregiro-overage" />
            </Stack>
            {formValues.allow_overage && (
              <TextField
                label="Precio del crédito en sobregiro (opcional)"
                value={formValues.overage_credit_price}
                onChange={handleChange('overage_credit_price')}
                type="number"
                inputProps={{ step: '0.01', min: 0 }}
                fullWidth
                size="small"
                sx={darkTextFieldStyle}
                InputProps={{ endAdornment: (
                  <InputAdornment position="end"><HelpIcon module="creditos" topic="configuracion-de-creditos" anchor="precio-del-crédito-en-sobregiro-opcional" /></InputAdornment>
                ) }}
              />
            )}
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <FormControlLabel
                control={<Switch checked={formValues.allow_rollover} onChange={handleToggle('allow_rollover')} />}
                label="Permitir rollover de créditos no usados"
              />
              <HelpIcon module="creditos" topic="configuracion-de-creditos" anchor="permitir-rollover-de-créditos-no-usados" />
            </Stack>
            {formValues.allow_rollover && (
              <TextField
                label="Límite de rollover (opcional)"
                value={formValues.rollover_limit}
                onChange={handleChange('rollover_limit')}
                type="number"
                inputProps={{ step: '0.01', min: 0 }}
                fullWidth
                size="small"
                sx={darkTextFieldStyle}
                InputProps={{ endAdornment: (
                  <InputAdornment position="end"><HelpIcon module="creditos" topic="configuracion-de-creditos" anchor="límite-de-rollover-opcional" /></InputAdornment>
                ) }}
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFormOpen(false)} disabled={isSaving}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={isSaving}
            startIcon={isSaving ? <CircularProgress color="inherit" size={16} /> : null}
            sx={{ bgcolor: 'var(--wb-primary)', '&:hover': { bgcolor: 'var(--wb-primary-hover)' } }}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        ContentProps={{
          sx: {
            backgroundColor:
              snackbar.severity === 'success' ? 'var(--wb-success-90)'
                : snackbar.severity === 'warning' ? 'var(--wb-warning-90)'
                  : 'var(--wb-danger-95)'
          }
        }}
      />
    </PageContainer>
  );
};

const BillingCreditConfig = () => (
  <RequireBillingRole allow="root" title="Configuración de créditos">
    <BillingCreditConfigContent />
  </RequireBillingRole>
);

export default BillingCreditConfig;
